import React from "react";
import { generateHSLGradient } from "../../utils/Colors/Colors";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Label,
  Tooltip,
} from "recharts";

interface DataPoint {
  name: string;
  value: number;
  startColor?: string;
  endColor?: string;
  id?: string;
}

interface GradientBarChartProps {
  data: DataPoint[];
  title: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
}

const GradientBarChart: React.FC<GradientBarChartProps> = ({
  data,
  title,
  xAxisTitle = "Data Elements",
  yAxisTitle = "Values",
}) => {
  // Assign gradient colors dynamically
  const updatedData = data?.map((entry, index) => ({
    ...entry,
    ...generateHSLGradient(index),
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="p-2 rounded text-white text-sm">
          <p>{`${data.name}: ${data.value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="size-full bg-[#10151A] ">
      <h2 className="text-white text-lg text-center mb-4">{title}</h2>
      <div className="h-[calc(100%-2rem)]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={updatedData}
            margin={{ top: 10, right: 30, left: 25, bottom: 40 }}
          >
            {/* Define unique gradients here */}
            <defs>
              {updatedData?.map(({ id, startColor, endColor }) => (
                <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={startColor} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={endColor} stopOpacity={0.3} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid
              //  strokeDasharray="3 3"
              horizontal={true} // Disable horizontal grid lines
              vertical={false} // Ensure vertical grid lines appear for all X-axis values
              stroke="#333"
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#fff" }}
            >
              <Label
                value={xAxisTitle}
                offset={-15}
                position="insideBottom"
                style={{ fill: "#fff", fontSize: "14px" }}
              />
            </XAxis>
            <YAxis axisLine={false} tickLine={false} tick={{ fill: "#fff" }}>
              <Label
                value={yAxisTitle}
                angle={-90}
                position="insideLeft"
                style={{ fill: "#fff", fontSize: "14px" }}
              />
            </YAxis>
            <Bar
              dataKey="value"
              radius={[8, 8, 0, 0]} // this rounds up the bar
              barSize={60}
            >
              {updatedData?.map(({ id }, index) => (
                <Cell key={`cell-${index}`} fill={`url(#${id})`} />
              ))}
            </Bar>
            <Tooltip content={<CustomTooltip />} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GradientBarChart;
